import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Form, Button, Dropdown } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { US_STATES } from "configurations/Constants";
import {
  PROFILE_FETCH_ERROR,
  PLEASE_ENTER_FIRST_NAME,
  PLEASE_ENTER_LAST_NAME,
  PLEASE_ENTER_TDA_NUMBER,
  PLEASE_ENTER_ADDRESS,
  PLEASE_ENTER_CITY,
  PLEASE_SELECT_STATE,
  PLEASE_ENTER_ZIP_CODE,
  PROFILE_UPDATE_ERROR,
  PLEASE_TRY_AGAIN_LATER,
  PROFILE_UPDATE_SUCCESSFUL,
  LABEL_ENTER_FIRST_NAME,
  LABEL_ENTER_LAST_NAME,
  LABEL_ENTER_TDA_NUMBER,
  LABEL_ENTER_MOBILE_NUMBER,
  LABEL_ENTER_ADDRESS,
  LABEL_ENTER_CITY,
  LABEL_ENTER_ZIP_CODE,
  LABEL_ENTER_EMAIL,
  LABEL_SELECT_STATE,
} from "configurations/MessageConstants";
import UserService from "services/UserService";

import BackDropLoader from "components/common/BackDropLoader";
import Lottie from "react-lottie";
import { USER_REGISTRATION_LOTTIE_OPTIONS } from "configurations/Constants";
import SuccessPopup from "components/common/SuccessPopup";
import ChangePasswordPopup from "components/user/profile/ChangePasswordPopup";
import { toast } from "react-toastify";
import PageSkeleton from "components/common/PageSkeleton";
import "styles/Profile.css";
import { setUser } from "reduxStore/slices/globalSlice";
import { useDispatch } from "react-redux";

const Profile = () => {
  const dispatch = useDispatch();
  const [originalFormData, setOriginalFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveProfileSuccess, setSaveProfileSuccess] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const defaultValues = {
    firstName: "",
    lastName: "",
    tdaNumber: "",
    email: "",
    mobileNumber: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues });

  useEffect(() => {
    const fetchUserProfile = async () => {
      setIsSaving(true);
      try {
        const userProfile = await UserService.getUserProfile();
        reset({
          firstName: userProfile.firstName,
          lastName: userProfile.lastName,
          tdaNumber: userProfile.tdaNumber,
          email: userProfile.email,
          mobileNumber: userProfile.mobileNumber,
          address: userProfile.address,
          city: userProfile.city,
          state: userProfile.state,
          zipCode: userProfile.zipCode,
        });
        setOriginalFormData(userProfile);
      } catch (error) {
        toast.error(PROFILE_FETCH_ERROR);
        console.error(PROFILE_FETCH_ERROR, error);
      } finally {
        setIsSaving(false);
      }
    };
    fetchUserProfile();
  }, [reset]);

  const handleEdit = () => {
    if (isEditing) {
      reset(originalFormData);
    }
    setIsEditing(!isEditing);
  };

  const handleUpdateProfile = async (data) => {
    try {
      await UserService.updateUserProfile(data);
      setIsEditing(false);
      setSaveProfileSuccess(true);
      dispatch(
        setUser({
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
        })
      );
    } catch (error) {
      console.error(PROFILE_UPDATE_ERROR, error);
      toast.error(
        <div>
          {PROFILE_UPDATE_ERROR}
          <br />
          {PLEASE_TRY_AGAIN_LATER}
        </div>
      );
    }
  };

  const handleChangePassword = () => {
    setShowChangePassword(true);
  };

  return (
    <div className="scrollable-container">
      {isSubmitting && (
        <BackDropLoader
          loader={
            <Lottie
              options={USER_REGISTRATION_LOTTIE_OPTIONS}
              height={200}
              width={200}
            />
          }
        />
      )}
      {isSaving ? (
        <PageSkeleton />
      ) : (
        <>
          <div className="d-flex align-items-center mb-3 justify-content-between">
            <h5 className="mb-3">Personal Information</h5>
            <Dropdown align="end" title="Profile Settings">
              <Dropdown.Toggle
                variant="link"
                className="p-0 border-0 shadow-none text-dark"
                aria-label="Profile settings menu"
                aria-expanded="false"
              >
                <i className="fa fa-cog fa-lg" aria-hidden="true"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ minWidth: "fit-content" }}>
                <Dropdown.Item onClick={handleChangePassword}>
                  <i className="fa fa-key me-2"></i> Change Password
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <Form onSubmit={handleSubmit(handleUpdateProfile)} noValidate>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{
                      required: PLEASE_ENTER_FIRST_NAME,
                      validate: (value) =>
                        value.trim() !== "" || PLEASE_ENTER_FIRST_NAME,
                    }}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type="text"
                        placeholder={LABEL_ENTER_FIRST_NAME}
                        className="input-underline"
                        isInvalid={!!errors.firstName}
                        disabled={!isEditing}
                        maxLength={50}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.firstName?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{
                      required: PLEASE_ENTER_LAST_NAME,
                      validate: (value) =>
                        value.trim() !== "" || PLEASE_ENTER_LAST_NAME,
                    }}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type="text"
                        placeholder={LABEL_ENTER_LAST_NAME}
                        className="input-underline"
                        isInvalid={!!errors.lastName}
                        disabled={!isEditing}
                        maxLength={50}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.lastName?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>TDA Number</Form.Label>
                  <Controller
                    name="tdaNumber"
                    control={control}
                    rules={{
                      required: PLEASE_ENTER_TDA_NUMBER,
                      validate: (value) =>
                        value.trim() !== "" || PLEASE_ENTER_TDA_NUMBER,
                    }}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type="text"
                        placeholder={LABEL_ENTER_TDA_NUMBER}
                        className="input-underline"
                        isInvalid={!!errors.tdaNumber}
                        disabled={!isEditing}
                        maxLength={50}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.tdaNumber?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type="email"
                        placeholder={LABEL_ENTER_EMAIL}
                        className="input-underline"
                        autoComplete="email"
                        disabled={true}
                      />
                    )}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number</Form.Label>
                  <Controller
                    name="mobileNumber"
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type="text"
                        placeholder={LABEL_ENTER_MOBILE_NUMBER}
                        className="input-underline"
                        disabled={true}
                      />
                    )}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Controller
                    name="address"
                    control={control}
                    rules={{
                      required: PLEASE_ENTER_ADDRESS,
                      validate: (value) =>
                        value.trim() !== "" || PLEASE_ENTER_ADDRESS,
                    }}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type="text"
                        placeholder={LABEL_ENTER_ADDRESS}
                        className="input-underline"
                        isInvalid={!!errors.address}
                        disabled={!isEditing}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.address?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Controller
                    name="city"
                    control={control}
                    rules={{
                      required: PLEASE_ENTER_CITY,
                      validate: (value) =>
                        value.trim() !== "" || PLEASE_ENTER_CITY,
                    }}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type="text"
                        placeholder={LABEL_ENTER_CITY}
                        className="input-underline"
                        isInvalid={!!errors.city}
                        disabled={!isEditing}
                        maxLength={50}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.city?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="state">State</Form.Label>
                  <Controller
                    name="state"
                    control={control}
                    rules={{ required: PLEASE_SELECT_STATE }}
                    render={({ field }) => (
                      <Form.Select
                        {...field}
                        id="state"
                        className="input-underline"
                        aria-label="Select State"
                        isInvalid={!!errors.state}
                        disabled={!isEditing}
                      >
                        <option value="">{LABEL_SELECT_STATE}</option>
                        {US_STATES.map((stateValue) => (
                          <option key={stateValue} value={stateValue}>
                            {stateValue}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.state?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Zip Code</Form.Label>
                  <Controller
                    name="zipCode"
                    control={control}
                    rules={{
                      required: PLEASE_ENTER_ZIP_CODE,
                      validate: (value) =>
                        value.trim() !== "" || PLEASE_ENTER_ZIP_CODE,
                    }}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type="text"
                        placeholder={LABEL_ENTER_ZIP_CODE}
                        className="input-underline"
                        isInvalid={!!errors.zipCode}
                        disabled={!isEditing}
                        maxLength={10}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.zipCode?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="outline-secondary" onClick={handleEdit}>
                {isEditing ? "Cancel" : "Edit"}
              </Button>
              <Button type="submit" disabled={!isEditing}>
                Update
              </Button>
            </div>
          </Form>
        </>
      )}
      {saveProfileSuccess && (
        <SuccessPopup
          message={PROFILE_UPDATE_SUCCESSFUL}
          show={saveProfileSuccess}
          handleClose={() => setSaveProfileSuccess(false)}
        />
      )}
      <ChangePasswordPopup
        show={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
    </div>
  );
};

export default Profile;
